import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { CARDS } from "@reagent/content";
import { useGameStore } from "../../app/gameStore";
import { PALETTE } from "../palette";
import { gridToWorld, TILE_SIZE } from "./gridToWorld";
import { useInteractionStore } from "./interactionStore";
import { moveTargetValid } from "./moveGhost";

/**
 * Snapping preview shown while a card is dragged over the board, or while a
 * Setup-phase catalogue offer is selected and the board is hovered. It snaps
 * tile-to-tile rather than following the cursor continuously — the snap is
 * what makes placement feel committed rather than approximate.
 */
export function PlacementGhost() {
  const hoveredTile = useInteractionStore((s) => s.hoveredTile);
  const draggedCardKind = useInteractionStore((s) => s.draggedCardKind);
  const draggedCardTarget = useInteractionStore((s) => s.draggedCardTarget);
  const phase = useGameStore((s) => s.snapshot.phase);
  const pendingInstall = useGameStore((s) => s.pendingInstall);
  // A machine offer reads as the solid tile-footprint ghost below. A pipe
  // offer joins two machines, so it takes the ring ghost a played pipe card
  // uses — never a block sitting on the square.
  const isSetupPlacement = phase === "setup" && pendingInstall !== null;
  const isSetupPipe = isSetupPlacement && Boolean(CARDS[pendingInstall.cardId]?.deploysPipe);
  // Spec 0067: a machine being moved reads as the same solid ghost, tinted
  // terracotta where the engine would refuse the landing tile.
  const pendingMove = useGameStore((s) => s.pendingMove);
  const snapshot = useGameStore((s) => s.snapshot);
  const isNodeMove = pendingMove?.kind === "node";
  const tint =
    isNodeMove && hoveredTile && !moveTargetValid(snapshot, hoveredTile) ? PALETTE.hazardCritical : PALETTE.mustard;
  const groupRef = useRef<THREE.Group>(null);
  const targetPos = useRef(new THREE.Vector3());
  const hasPositioned = useRef(false);

  useEffect(() => {
    if (!hoveredTile) return;
    const [x, , z] = gridToWorld(hoveredTile);
    targetPos.current.set(x, 0, z);
  }, [hoveredTile]);

  // Reset on pick-up so the next drag snaps in place rather than sliding in
  // from wherever the previous drag ended.
  useEffect(() => {
    if (!draggedCardKind && !isNodeMove) hasPositioned.current = false;
  }, [draggedCardKind, isNodeMove]);

  useFrame(({ clock }, delta) => {
    const group = groupRef.current;
    if (!group) return;
    if (!hasPositioned.current) {
      group.position.copy(targetPos.current);
      hasPositioned.current = true;
    } else {
      // Snap quickly toward the tile, then bob so it reads as "held", not placed.
      group.position.lerp(targetPos.current, Math.min(delta * 22, 1));
    }
    group.position.y = 0.16 + Math.sin(clock.elapsedTime * 4) * 0.03;
  });

  if (!hoveredTile || (!draggedCardKind && !isSetupPlacement && !isNodeMove)) return null;

  // Attachments and pipes fasten to hardware that is already on the tile, so
  // a solid block sitting in the square says the wrong thing entirely. Ring
  // the machine instead: "this one", not "here".
  if (!isNodeMove && (isSetupPipe || (!isSetupPlacement && draggedCardTarget !== "tile"))) {
    return (
      <group ref={groupRef}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={900}>
          <ringGeometry args={[TILE_SIZE * 0.46, TILE_SIZE * 0.58, 28]} />
          <meshBasicMaterial
            color={PALETTE.mustard}
            transparent
            opacity={0.95}
            side={THREE.DoubleSide}
            depthWrite={false}
            depthTest={false}
          />
        </mesh>
      </group>
    );
  }

  return (
    <group ref={groupRef}>
      {/* Solid-ish body so the ghost reads against the warm board, not just a
          tint on it. depthWrite off keeps it from z-fighting the tile seams. */}
      <mesh position={[0, 0.26, 0]}>
        <boxGeometry args={[TILE_SIZE * 0.62, 0.52, TILE_SIZE * 0.62]} />
        <meshBasicMaterial color={tint} transparent opacity={0.72} depthWrite={false} />
      </mesh>
      {/* Crisp wireframe edge, matching the game's thick line-art language. */}
      <mesh position={[0, 0.26, 0]}>
        <boxGeometry args={[TILE_SIZE * 0.64, 0.54, TILE_SIZE * 0.64]} />
        <meshBasicMaterial color="#2B2118" wireframe transparent opacity={0.85} />
      </mesh>
      {/* Footprint square on the tile itself, so the snap target is unambiguous. */}
      <mesh position={[0, -0.13, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TILE_SIZE * 0.92, TILE_SIZE * 0.92]} />
        <meshBasicMaterial color={tint} transparent opacity={0.55} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
    </group>
  );
}
